import React, { useEffect, useState } from 'react';
import { FaTelegramPlane, FaShieldAlt, FaRobot, FaChartLine, FaGlobe, FaLock, FaHeadset, FaSearch, FaHandshake, FaUserPlus, FaWallet, FaBolt } from 'react-icons/fa';
import { FiZap, FiTrendingUp, FiActivity, FiClock } from 'react-icons/fi';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import TawkToChat from '../components/TawkToChat';

const DEFAULT_SUPPORT = {
  telegram: { username: 'Gemini_AI_com', displayName: 'Gemini AI Support', display: 'Gemini AI Support' },
  email: { address: 'support@geminiai.trade', displayName: 'Support Team' },
  hours: '24 / 7 — Always online for our traders'
};

const HOW_IT_WORKS = [
  {
    icon: <FaUserPlus className="h-5 w-5" />,
    step: '01',
    title: 'Create Your Account',
    desc: 'Sign up in under a minute. Complete a quick verification to unlock full platform access and higher limits.',
    color: 'from-cyan-500 to-blue-500'
  },
  {
    icon: <FaWallet className="h-5 w-5" />,
    step: '02',
    title: 'Fund & Allocate',
    desc: 'Deposit your preferred asset, then transfer to the trading or arbitrage account that fits your strategy.',
    color: 'from-blue-500 to-indigo-500'
  },
  {
    icon: <FaBolt className="h-5 w-5" />,
    step: '03',
    title: 'Trade or Earn',
    desc: 'Place leveraged contract trades or activate a managed arbitrage plan. Watch your portfolio grow in real time.',
    color: 'from-indigo-500 to-purple-500'
  },
];

const FEATURES = [
  { icon: <FiZap className="h-5 w-5" />, title: 'AI-Powered Signals', desc: 'Adaptive models surface high-probability entries across 100+ assets in real time.', color: 'from-cyan-500 to-blue-500' },
  { icon: <FaChartLine className="h-5 w-5" />, title: 'Contract Trading', desc: 'Execute leveraged positions with institutional-grade risk controls and live P&L tracking.', color: 'from-blue-500 to-indigo-500' },
  { icon: <FiTrendingUp className="h-5 w-5" />, title: 'Arbitrage Engine', desc: 'Earn steady returns through cross-exchange spread capture with fully managed hedging.', color: 'from-indigo-500 to-purple-500' },
  { icon: <FaRobot className="h-5 w-5" />, title: 'Automated Strategies', desc: 'Deploy pre-built playbooks or connect custom logic via our automation stack.', color: 'from-purple-500 to-pink-500' },
  { icon: <FaShieldAlt className="h-5 w-5" />, title: 'Institutional Security', desc: 'Multi-layer encryption, SOC2-ready infrastructure, and segregated client funds.', color: 'from-emerald-500 to-teal-500' },
  { icon: <FiActivity className="h-5 w-5" />, title: 'Real-Time Analytics', desc: 'Deep attribution dashboards with compliance-ready exports and portfolio breakdowns.', color: 'from-amber-500 to-orange-500' },
];

const VALUES = [
  { icon: <FaSearch className="h-5 w-5" />, title: 'Transparency', desc: 'Clear, honest communication. No hidden fees, no surprises.', color: 'from-cyan-500 to-blue-500' },
  { icon: <FaLock className="h-5 w-5" />, title: 'Security', desc: 'Your funds and data are protected by top-tier security protocols.', color: 'from-emerald-500 to-teal-500' },
  { icon: <FiZap className="h-5 w-5" />, title: 'Innovation', desc: 'We continuously push technology forward to stay ahead in crypto.', color: 'from-amber-500 to-orange-500' },
  { icon: <FaHandshake className="h-5 w-5" />, title: 'User-Centric', desc: 'Every feature is built with the end-user experience as the top priority.', color: 'from-indigo-500 to-purple-500' },
];

const AboutUs = () => {
  const [support, setSupport] = useState(DEFAULT_SUPPORT);

  // FIX 2: Real-time customer service config with onSnapshot
  useEffect(() => {
    const ref = doc(db, 'config', 'customerService');
    
    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          
          // Handle telegram - can have username or displayName
          const telegramUsername = data.telegram?.username || data.telegram?.displayName || DEFAULT_SUPPORT.telegram.username;
          const telegramDisplay = data.telegram?.displayName || data.telegram?.display || telegramUsername || DEFAULT_SUPPORT.telegram.display;
          
          // Handle email - can be plain string or { address: "..." } object
          let emailAddress = DEFAULT_SUPPORT.email.address;
          let emailDisplay = data.email?.displayName || DEFAULT_SUPPORT.email.displayName;
          if (data.email) {
            if (typeof data.email === 'string') {
              emailAddress = data.email;
            } else if (data.email.address) {
              emailAddress = data.email.address;
            }
          }
          
          setSupport({
            telegram: {
              username: telegramUsername,
              display: telegramDisplay,
              displayName: telegramDisplay
            },
            email: {
              address: emailAddress,
              displayName: emailDisplay
            },
            hours: data.hours || DEFAULT_SUPPORT.hours
          });
        }
      },
      (err) => {
        console.error('Failed to load support config:', err);
      }
    );
    
    return () => unsubscribe();
  }, []);
  
  // Helper to build Telegram URL from username (remove @ if present)
  const getTelegramUrl = (username) => {
    const cleanUsername = username?.replace(/^@/, '') || 'support';
    return `https://t.me/${cleanUsername}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-navy to-slate-950 text-slate-50 pb-28">

      {/* Hero */}
      <section className="relative overflow-hidden px-6 py-20 text-center">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(99,102,241,0.15),transparent_70%)]" />
        <div className="relative z-10 mx-auto max-w-3xl space-y-5">
          <span className="inline-flex items-center gap-2 rounded-full border border-indigo-500/30 bg-indigo-500/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.25em] text-indigo-300">
            <FaGlobe className="h-3 w-3" /> GeminiAI Trading Platform
          </span>
          <h1 className="text-4xl font-extrabold leading-tight tracking-tight text-white sm:text-5xl">
            The Smartest Way to<br />
            <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-indigo-400 bg-clip-text text-transparent">
              Trade Crypto
            </span>
          </h1>
          <p className="text-base text-slate-400 sm:text-lg leading-relaxed">
            GeminiAI Trading combines institutional-grade infrastructure with AI-driven strategies,
            giving every trader access to tools previously reserved for hedge funds.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-6 pt-2 text-sm text-slate-400">
            {['100+ Assets', 'AI-Powered Engine', 'Fully Managed Arbitrage', '24/7 Support'].map(tag => (
              <span key={tag} className="flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-indigo-400" />
                {tag}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="mx-auto max-w-5xl px-6">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {[
            { label: 'Assets Supported', value: '100+' },
            { label: 'Platform Uptime', value: '99.9%' },
            { label: 'Arbitrage Plans', value: '6' },
            { label: 'Live Support', value: '24 / 7' },
          ].map(({ label, value }) => (
            <div key={label} className="rounded-2xl border border-slate-700/40 bg-slate-900/60 p-5 text-center">
              <p className="text-2xl font-extrabold text-white">{value}</p>
              <p className="mt-1 text-xs uppercase tracking-widest text-slate-500">{label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Mission */}
      <section className="mx-auto mt-16 max-w-5xl px-6">
        <div className="rounded-2xl border border-slate-700/30 bg-gradient-to-br from-slate-900/80 to-indigo-950/30 p-8">
          <h2 className="mb-3 text-xs font-bold uppercase tracking-widest text-indigo-400">Our Mission</h2>
          <p className="text-lg leading-relaxed text-slate-300">
            GeminiAI Trading exists to bring professional-grade market access to every trader. We pair institutional infrastructure with AI-driven execution, transparent reporting, and world-class security so that individuals can build long-term wealth with the same precision once reserved for hedge funds and proprietary desks.
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section className="mx-auto mt-16 max-w-5xl px-6">
        <div className="mb-6 flex items-end justify-between gap-3">
          <div>
            <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500">How It Works</h2>
            <p className="mt-1 text-base font-semibold text-white">Get started in three simple steps</p>
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          {HOW_IT_WORKS.map(({ icon, step, title, desc, color }) => (
            <div key={step} className="relative overflow-hidden rounded-2xl border border-slate-700/30 bg-slate-900/50 p-6 transition-all hover:border-slate-600/50 hover:bg-slate-900/80">
              <span className="absolute right-4 top-4 text-xs font-bold tracking-widest text-slate-700">{step}</span>
              <div className={`mb-4 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${color} text-white shadow-md`}>
                {icon}
              </div>
              <h3 className="text-sm font-bold text-white">{title}</h3>
              <p className="mt-1.5 text-xs leading-relaxed text-slate-400">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto mt-16 max-w-5xl px-6">
        <h2 className="mb-6 text-xs font-bold uppercase tracking-widest text-slate-500">Platform Features</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map(({ icon, title, desc, color }) => (
            <div key={title} className="group rounded-2xl border border-slate-700/30 bg-slate-900/50 p-5 transition-all hover:border-slate-600/50 hover:bg-slate-900/80">
              <div className={`mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${color} text-white shadow-md`}>
                {icon}
              </div>
              <h3 className="text-sm font-bold text-white">{title}</h3>
              <p className="mt-1 text-xs leading-relaxed text-slate-400">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="mx-auto mt-16 max-w-5xl px-6">
        <h2 className="mb-6 text-xs font-bold uppercase tracking-widest text-slate-500">Our Values</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {VALUES.map(({ icon, title, desc, color }) => (
            <div key={title} className="rounded-2xl border border-slate-700/30 bg-slate-900/50 p-5">
              <div className={`inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${color} text-white shadow-md`}>
                {icon}
              </div>
              <h3 className="mt-3 text-sm font-bold text-white">{title}</h3>
              <p className="mt-1 text-xs leading-relaxed text-slate-400">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Security callout */}
      <section className="mx-auto mt-16 max-w-5xl px-6">
        <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-6">
          <div className="flex items-start gap-5">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-400 text-xl">
              <FaLock />
            </div>
            <div>
              <h3 className="text-sm font-bold text-emerald-300">Your Funds Are Secure</h3>
              <p className="mt-1 text-xs leading-relaxed text-slate-400">
                Client funds are held in segregated accounts with multi-signature protection.
                Our platform uses AES-256 encryption, rate limiting, and continuous threat monitoring to keep your capital safe around the clock.
              </p>
            </div>
          </div>
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { label: 'Cold Storage', value: '95%' },
              { label: 'Encryption', value: 'AES-256' },
              { label: '2FA', value: 'Enforced' },
              { label: 'Audits', value: 'Quarterly' },
            ].map(({ label, value }) => (
              <div key={label} className="rounded-xl border border-emerald-500/15 bg-emerald-500/[0.04] p-3 text-center">
                <p className="text-sm font-bold text-emerald-200">{value}</p>
                <p className="mt-0.5 text-[10px] uppercase tracking-widest text-emerald-400/70">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Support */}
      <section className="mx-auto mt-16 max-w-5xl px-6">
        <h2 className="mb-6 text-xs font-bold uppercase tracking-widest text-slate-500">Customer Support</h2>
        <div className="grid gap-4 sm:grid-cols-3">

          {/* Telegram card */}
          <div className="flex flex-col gap-4 rounded-2xl border border-blue-500/20 bg-gradient-to-br from-blue-900/30 to-slate-900/60 p-6">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-500/20 text-blue-300 text-2xl">
              <FaTelegramPlane />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-blue-400 mb-1">Telegram</p>
              <p className="text-sm font-bold text-white">{support.telegram.display}</p>
              <p className="text-xs text-slate-400 mt-0.5">{support.telegram.username}</p>
            </div>
            <a
              href={getTelegramUrl(support.telegram.username)}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-auto inline-flex items-center gap-2 rounded-lg border border-blue-500/30 bg-blue-500/10 px-4 py-2.5 text-sm font-semibold text-blue-200 hover:bg-blue-500/20 transition-colors cursor-pointer"
            >
              <FaTelegramPlane className="h-4 w-4" />
              Message on Telegram
            </a>
          </div>

          {/* Email card */}
          <div className="flex flex-col gap-4 rounded-2xl border border-indigo-500/20 bg-gradient-to-br from-indigo-900/30 to-slate-900/60 p-6">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-500/20 text-indigo-300 text-2xl">
              <FaHeadset />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-indigo-400 mb-1">Email</p>
              <p className="text-sm font-bold text-white">Support Team</p>
              <p className="text-xs text-slate-400 mt-0.5">{support.email.address}</p>
            </div>
            <a
              href={`mailto:${support.email.address}`}
              className="mt-auto inline-flex items-center gap-2 rounded-lg border border-indigo-500/30 bg-indigo-500/10 px-4 py-2.5 text-sm font-semibold text-indigo-200 hover:bg-indigo-500/20 transition-colors cursor-pointer"
            >
              <FaHeadset className="h-4 w-4" />
              Email Support
            </a>
          </div>

          {/* Hours card */}
          <div className="flex flex-col gap-4 rounded-2xl border border-emerald-500/20 bg-gradient-to-br from-emerald-900/20 to-slate-900/60 p-6">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-500/20 text-emerald-300">
              <FiClock className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-emerald-400 mb-1">Availability</p>
              <p className="text-sm font-bold text-white">Always Online</p>
              <p className="text-xs text-slate-400 mt-0.5">{support.hours}</p>
            </div>
            <div className="mt-auto inline-flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 text-sm font-semibold text-emerald-200 cursor-default select-none">
              <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
              Live Support Active
            </div>
          </div>

        </div>
      </section>

      {/* Closing CTA */}
      <section className="mx-auto mt-16 max-w-5xl px-6">
        <div className="relative overflow-hidden rounded-2xl border border-indigo-500/30 bg-gradient-to-br from-indigo-900/40 via-slate-900/60 to-blue-900/30 p-8 text-center">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(99,102,241,0.15),transparent_70%)]" />
          <div className="relative z-10 mx-auto max-w-2xl space-y-3">
            <h2 className="text-2xl font-extrabold text-white sm:text-3xl">Ready to trade smarter?</h2>
            <p className="text-sm leading-relaxed text-slate-300 sm:text-base">
              Join traders worldwide who use GeminiAI Trading to access institutional-grade strategies, transparent execution,
              and a fully managed arbitrage engine — all from one platform.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 pt-2 text-xs text-slate-400">
              <span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> KYC Verified Platform</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-cyan-400" /> Regulated Custody Partners</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-indigo-400" /> 24/7 Live Support</span>
            </div>
          </div>
        </div>
      </section>

      {/* Custom AI Chat - Only on About Page */}
      <TawkToChat />
    </div>
  );
};

export default AboutUs;
